import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Phone, ArrowDown, ArrowUpRight } from "lucide-react";
import { SITE, IMAGES } from "@/constants/site";
import { MaskedLine } from "@/components/Reveal";
import { scrollToId } from "@/lib/scroll";

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "30%"]);
  const fade = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  return (
    <section
      ref={ref}
      data-testid="hero-section"
      className="relative flex min-h-[100svh] flex-col justify-end overflow-hidden bg-ink"
    >
      <motion.div style={{ y: imgY }} className="absolute right-0 top-0 h-[115%] w-full md:w-[48%]">
        <div className="h-full w-full opacity-25 [clip-path:polygon(16%_0,100%_0,100%_100%,0_100%)] md:opacity-45">
          <img
            src={IMAGES.junkPile}
            alt="A pile of junk ready for removal"
            className="h-full w-full object-cover grayscale"
            loading="eager"
          />
        </div>
        <div className="absolute inset-0 bg-gradient-to-r from-ink via-ink/70 to-ink/10" />
      </motion.div>

      <div className="pointer-events-none absolute left-0 top-24 hidden gap-1 md:flex" aria-hidden="true">
        <span className="block h-1 w-24 -skew-x-12 bg-acid" />
        <span className="block h-1 w-16 -skew-x-12 bg-acid" />
        <span className="block h-1 w-8 -skew-x-12 bg-acid" />
      </div>

      <motion.div
        style={{ opacity: fade }}
        className="relative z-10 mx-auto flex min-h-[100svh] w-full max-w-7xl flex-col justify-end px-6 pb-10 pt-32 md:px-10"
      >
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.95, duration: 0.6 }}
          className="mb-6 text-xs font-bold uppercase tracking-[0.35em] text-acid"
        >
          {SITE.area} · {SITE.hours}
        </motion.p>

        <h1 className="font-display text-6xl font-black italic uppercase leading-[0.85] tracking-tighter sm:text-7xl lg:text-[7vw]">
          <MaskedLine delay={0.1}>
            <span className="block">We take</span>
          </MaskedLine>
          <MaskedLine delay={0.25}>
            <span className="block text-acid">the junk.</span>
          </MaskedLine>
          <MaskedLine delay={0.4}>
            <span className="block">You start</span>
          </MaskedLine>
          <MaskedLine delay={0.55}>
            <span className="block">
              fresh<span className="text-acid">.</span>
            </span>
          </MaskedLine>
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.8, duration: 0.6 }}
          className="mt-6 max-w-md text-base leading-relaxed text-zinc-400"
        >
          Same-day junk removal for homes and businesses. Upfront pricing,
          we do all the lifting, and we leave the space swept clean.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.95, duration: 0.6 }}
          className="mt-10 flex flex-wrap items-center gap-4"
        >
          <button
            data-testid="hero-quote-btn"
            onClick={() => scrollToId("quote")}
            className="group flex items-center gap-2 bg-acid px-8 py-4 font-display text-xl font-extrabold italic uppercase tracking-tight text-black transition-colors duration-200 hover:bg-white"
          >
            Get a free quote
            <ArrowUpRight className="h-5 w-5 transition-transform duration-200 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
          </button>
          <a
            data-testid="hero-call-btn"
            href={`tel:${SITE.phoneTel}`}
            className="flex items-center gap-3 border border-zinc-700 px-8 py-4 font-display text-xl font-extrabold italic uppercase tracking-tight text-white transition-colors duration-200 hover:border-acid hover:text-acid"
          >
            <Phone className="h-5 w-5" />
            Call {SITE.phoneDisplay}
          </a>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.3 }}
          className="mt-8 flex items-center gap-3 text-[10px] font-bold uppercase tracking-[0.3em] text-zinc-600"
        >
          <ArrowDown className="h-3.5 w-3.5 animate-bounce" />
          Scroll — see how it works
        </motion.div>
      </motion.div>
    </section>
  );
};
